import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Switch, Dropdown, ConfigProvider } from 'antd';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import axios from 'axios';

import { editUser, resendConfirmEmail, signedUploadUrl, uploadUserAvi } from '../../../../actions';
import { languageLookup, SUPPORTED_LOCALES } from '../../../../_locale/localesLookupTable';
import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import McpConnectGuide from '../../organisms/McpConnectGuide';
import ConnectedAppsSettings from '../../organisms/ConnectedAppsSettings';
import PersonalAccessTokensSettings from '../../organisms/PersonalAccessTokensSettings';

const API_URL = process.env.REACT_APP_API_URL;
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DEFAULT_EMAIL_PREFS = {
  taskAssigned: true, taskCompleted: true, dueDateChanged: true,
  comments: true, mentions: true, likes: false,
  dailyAgenda: true, weeklyOverdue: true,
};
const DEFAULT_DND = { enabled: false, from: '17:00', to: '09:00', days: [] };
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TABS = ['profile', 'notifications', 'integrations'];

/* ─── Time options in 30-min steps ──────────────────────────────────────── */
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2).toString().padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

const TimeSelect = ({ value, onChange }) => {
  const items = TIME_OPTIONS.map(t => ({
    key: t,
    label: (
      <span style={{
        fontSize: 13,
        color: t === value ? '#6366f1' : 'rgba(15,15,25,0.8)',
        fontWeight: t === value ? 600 : 400,
      }}>
        {t}
      </span>
    ),
    onClick: () => onChange(t),
  }));

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items,
        style: { maxHeight: 200, overflow: 'auto' },
        selectedKeys: [value],
      }}
      placement="bottomLeft"
    >
      <button style={{
        flex: 1, width: '100%', height: 30, padding: '0 8px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'white', border: '1px solid rgba(15,15,25,0.15)',
        borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'rgba(15,15,25,0.82)',
      }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,15,25,0.15)'}
      >
        <span>{value}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ color: 'rgba(99,102,241,0.5)', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    </Dropdown>
  );
};

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'America/Vancouver', 'America/Sao_Paulo', 'America/Mexico_City',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Moscow', 'Europe/Istanbul', 'Europe/Warsaw', 'Europe/Kyiv',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Shanghai',
  'Asia/Tokyo', 'Asia/Seoul', 'Asia/Singapore', 'Asia/Karachi', 'Asia/Tashkent',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland', 'Pacific/Auckland',
];

const TZ_LABEL = (tz) => {
  try {
    const offset = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
    return `${tz.replace(/_/g, ' ')} (${offset})`;
  } catch { return tz; }
};

/* ─── Shared primitives ──────────────────────────────────────────────────── */
const Label = ({ children }) => (
  <p style={{ fontSize: 12, color: 'rgba(15,15,25,0.45)', marginBottom: 5, fontWeight: 500 }}>
    {children}
  </p>
);

const FieldRow = ({ children, gap = 12 }) => (
  <div style={{ display: 'flex', gap, alignItems: 'flex-start' }}>
    {children}
  </div>
);

const inputStyle = (focused, error) => ({
  width: '100%',
  height: 32,
  padding: '0 10px',
  fontSize: 13,
  color: 'rgba(15,15,25,0.85)',
  background: 'white',
  border: `1px solid ${error ? '#ef4444' : focused ? 'rgba(99,102,241,0.5)' : 'rgba(15,15,25,0.15)'}`,
  borderRadius: 6,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
});

const FocusInput = ({ value, onChange, placeholder, readOnly, onBlur, onKeyDown, autoFocus, type = 'text', error = false }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      autoFocus={autoFocus}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); onBlur && onBlur(); }}
      onKeyDown={onKeyDown}
      style={inputStyle(focused, error)}
    />
  );
};

/* ─── Accordion ──────────────────────────────────────────────────────────── */
const AccordionSection = ({ icon, title, subtitle, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  return (
    <div style={{ border: '1px solid rgba(15,15,25,0.1)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: open ? 'rgba(99,102,241,0.04)' : 'white',
          border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ flexShrink: 0, color: '#6366f1' }}>{icon}</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(15,15,25,0.82)' }}>{title}</span>
          {subtitle && <span style={{ display: 'block', fontSize: 11, color: 'rgba(15,15,25,0.38)', marginTop: 1 }}>{subtitle}</span>}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, color: 'rgba(99,102,241,0.45)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && <div style={{ borderTop: '1px solid rgba(15,15,25,0.08)' }}>{children}</div>}
    </div>
  );
};

/* ─── Profile Tab ────────────────────────────────────────────────────────── */
const ProfileTab = ({ user, onClose }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [name, setName] = useState(user?.fullName || '');
  const [editingName, setEditingName] = useState(false);
  const [newPicture, setNewPicture] = useState(null);
  const [resent, setResent] = useState(false);
  const [notifEmail, setNotifEmail] = useState('');
  const [notifEmailError, setNotifEmailError] = useState('');
  const [notifEmailTouched, setNotifEmailTouched] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [savingExtra, setSavingExtra] = useState(false);

  const validateEmail = (val) => {
    if (!val) return '';
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val.trim()) ? '' : 'Enter a valid email address';
  };
  const [selectedLocale, setSelectedLocale] = useState(user?.locale || navigator.language || 'en');

  const handleLocaleUpdate = (locale) => {
    setSelectedLocale(locale);
    window.dispatchEvent(new CustomEvent('locale.change', { detail: locale }));
    dispatch(editUser({ _id: user._id, locale }));
  };

  useEffect(() => {
    axios.get(`${API_URL}/users/preferences`, cfg()).then(({ data }) => {
      setNotifEmail(data?.data?.notificationEmail || '');
      setTimezone(data?.data?.timezone || 'UTC');
    }).catch(() => {});
  }, []);

  const handleSaveName = async () => {
    if (name.trim() && name.trim() !== user?.fullName) {
      await dispatch(editUser({ _id: user._id, fullName: name.trim() }));
    }
    setEditingName(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setNewPicture(ev.target.result);
    reader.readAsDataURL(file);
    const { signedUrl, path } = await dispatch(uploadUserAvi({ fileName: file.name, type: file.type, fileSize: file.size }));
    if (signedUrl) await dispatch(signedUploadUrl(signedUrl, file));
    await dispatch(editUser({ _id: user._id, avi: path }));
  };

  const handleResend = () => { dispatch(resendConfirmEmail(user)); setResent(true); };

  const saveExtra = async (tz, ne) => {
    if (validateEmail(ne)) return;
    setSavingExtra(true);
    try {
      await axios.post(`${API_URL}/users/preferences`, { timezone: tz, notificationEmail: ne }, cfg());
    } catch (_) {}
    setSavingExtra(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Avatar row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          onClick={() => {
            const inp = document.createElement('input');
            inp.type = 'file'; inp.accept = 'image/jpeg,image/png';
            inp.onchange = handleFileChange; inp.click();
          }}
          style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
          className="group"
        >
          <UserAvatar fullName={user?.fullName} userId={user?._id} src={newPicture || user?.avi}
            styles={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 10, background: 'rgba(99,102,241,0.42)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = 1}
            onMouseLeave={e => e.currentTarget.style.opacity = 0}
          >
            <svg width="15" height="15" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>
        </div>
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(15,15,25,0.82)', marginBottom: 2 }}>
            {intl.formatMessage({ id: 'PROFILE_PHOTO', defaultMessage: 'Profile photo' })}
          </p>
          <p style={{ fontSize: 12, color: 'rgba(15,15,25,0.38)' }}>
            {intl.formatMessage({ id: 'PHOTO_HELP', defaultMessage: 'Click to upload a new photo' })}
          </p>
        </div>
      </div>

      {/* Full name */}
      <div>
        <Label>{intl.formatMessage({ id: 'FULL_NAME', defaultMessage: 'Full name' })}</Label>
        {editingName ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <FocusInput autoFocus value={name} onChange={e => setName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setName(user?.fullName || ''); } }} />
            <button onClick={handleSaveName} style={{ height: 32, padding: '0 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {intl.formatMessage({ id: 'SAVE', defaultMessage: 'Save' })}
            </button>
            <button onClick={() => { setEditingName(false); setName(user?.fullName || ''); }} style={{ height: 32, padding: '0 10px', background: 'rgba(15,15,25,0.06)', color: 'rgba(15,15,25,0.5)', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
              {intl.formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
            </button>
          </div>
        ) : (
          <button onClick={() => setEditingName(true)} style={{
            width: '100%', height: 32, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'white', border: '1px solid rgba(15,15,25,0.15)', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'rgba(15,15,25,0.82)',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,15,25,0.15)'}
          >
            <span>{user?.fullName}</span>
            <svg width="12" height="12" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24" style={{ opacity: 0.4 }}>
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </div>
      <div>
        <Label>{intl.formatMessage({ id: 'EMAIL', defaultMessage: 'Email' })}</Label>
        <div style={{ height: 32, padding: '0 10px', display: 'flex', alignItems: 'center', background: 'rgba(15,15,25,0.03)', border: '1px solid rgba(15,15,25,0.1)', borderRadius: 6, gap: 8 }}>
          <span style={{ flex: 1, fontSize: 13, color: 'rgba(15,15,25,0.5)' }}>{user?.email}</span>
          {!user?.isVerified && (
            resent
              ? <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 500 }}>{intl.formatMessage({ id: 'EMAIL_SENT', defaultMessage: 'Sent!' })}</span>
              : <button onClick={handleResend} style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {intl.formatMessage({ id: 'VERIFY', defaultMessage: 'Verify' })}
                </button>
          )}
          {user?.isVerified && (
            <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6.5l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {intl.formatMessage({ id: 'VERIFIED', defaultMessage: 'Verified' })}
            </span>
          )}
        </div>
      </div>
      <div>
        <Label>{intl.formatMessage({ id: 'NOTIF_EMAIL', defaultMessage: 'Notification email (optional)' })}</Label>
        <FocusInput
          value={notifEmail}
          onChange={e => {
            const val = e.target.value;
            setNotifEmail(val);
            if (notifEmailTouched) setNotifEmailError(validateEmail(val));
          }}
          placeholder={user?.email}
          error={!!notifEmailError}
          onBlur={() => {
            setNotifEmailTouched(true);
            const err = validateEmail(notifEmail);
            setNotifEmailError(err);
            if (!err) saveExtra(timezone, notifEmail);
          }}
        />
        {notifEmailError
          ? <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{notifEmailError}</p>
          : <p style={{ fontSize: 11, color: 'rgba(15,15,25,0.35)', marginTop: 4 }}>
              {intl.formatMessage({ id: 'NOTIF_EMAIL_HELP', defaultMessage: 'If set, all email notifications will be sent here instead.' })}
            </p>
        }
      </div>
      <div>
        <Label>{intl.formatMessage({ id: 'TIMEZONE', defaultMessage: 'Timezone' })}</Label>
        <Dropdown
          trigger={['click']}
          menu={{
            items: COMMON_TIMEZONES.map(tz => ({ key: tz, label: TZ_LABEL(tz), onClick: () => { setTimezone(tz); saveExtra(tz, notifEmail); } })),
            style: { maxHeight: 240, overflow: 'auto' },
          }}
          placement="bottomLeft"
        >
          <button style={{
            width: '100%', height: 32, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'white', border: '1px solid rgba(15,15,25,0.15)', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'rgba(15,15,25,0.8)',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,15,25,0.15)'}
          >
            <span>{TZ_LABEL(timezone)}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'rgba(99,102,241,0.5)', flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </Dropdown>
      </div>
      <div>
        <Label>{intl.formatMessage({ id: 'LANGUAGE', defaultMessage: 'Language' })}</Label>
        <Dropdown
          trigger={['click']}
          menu={{
            items: SUPPORTED_LOCALES.map(locale => ({ key: locale, label: languageLookup[locale], onClick: () => handleLocaleUpdate(locale) })),
            style: { maxHeight: 240, overflow: 'auto' },
          }}
          placement="bottomLeft"
        >
          <button style={{
            width: '100%', height: 32, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'white', border: '1px solid rgba(15,15,25,0.15)', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'rgba(15,15,25,0.8)',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,15,25,0.15)'}
          >
            <span>{languageLookup[selectedLocale]}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'rgba(99,102,241,0.5)', flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </Dropdown>
      </div>
    </div>
  );
};

/* ─── Notifications Tab ──────────────────────────────────────────────────── */
const PAUSE_OPTIONS = [
  { key: '30m', label: '30 minutes' },
  { key: '1h',  label: '1 hour' },
  { key: '2h',  label: '2 hours' },
  { key: '4h',  label: '4 hours' },
  { key: '8h',  label: '8 hours' },
  { key: 'tomorrow', label: 'Until tomorrow 9am' },
];

const NotificationsTab = ({ onSavingChange }) => {
  const intl = useIntl();
  const [emailPrefs, setEmailPrefs] = useState(DEFAULT_EMAIL_PREFS);
  const [dnd, setDnd] = useState(DEFAULT_DND);
  const [pausedUntil, setPausedUntil] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/users/preferences`, cfg()).then(({ data }) => {
      const ep = data?.data?.emailNotifications;
      const d = data?.data?.doNotDisturb;
      if (ep) setEmailPrefs({ ...DEFAULT_EMAIL_PREFS, ...ep });
      if (d) {
        setDnd({ ...DEFAULT_DND, ...d });
        if (d.pausedUntil && new Date(d.pausedUntil) > new Date()) {
          setPausedUntil(new Date(d.pausedUntil));
        }
      }
    }).catch(() => {});
  }, []);

  const save = useCallback(async (nextEmail, nextDnd) => {
    onSavingChange?.('saving');
    try {
      await axios.post(`${API_URL}/users/preferences`, { emailNotifications: nextEmail, doNotDisturb: nextDnd }, cfg());
      onSavingChange?.('saved');
      setTimeout(() => onSavingChange?.('idle'), 1800);
    } catch (_) { onSavingChange?.('idle'); }
  }, [onSavingChange]);

  const toggleEmail = (key) => {
    const next = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(next);
    save(next, dnd);
  };

  const toggleDndEnabled = () => {
    const next = { ...dnd, enabled: !dnd.enabled };
    setDnd(next);
    save(emailPrefs, next);
  };

  const toggleDndDay = (day) => {
    const days = dnd.days.includes(day) ? dnd.days.filter(d => d !== day) : [...dnd.days, day];
    const next = { ...dnd, days };
    setDnd(next);
    save(emailPrefs, next);
  };

  const handleDndTime = (field, value) => {
    const next = { ...dnd, [field]: value };
    setDnd(next);
    save(emailPrefs, next);
  };

  const handlePause = async (duration) => {
    try {
      const { data } = await axios.post(`${API_URL}/users/dnd-pause`, { duration }, cfg());
      const until = data?.data?.pausedUntil ? new Date(data.data.pausedUntil) : null;
      setPausedUntil(until);
    } catch (_) {}
  };

  const handleResume = () => handlePause('resume');

  const isPaused = pausedUntil && pausedUntil > new Date();

  const fmtPausedUntil = (d) => {
    if (!d) return '';
    const h = d.getHours(), m = d.getMinutes();
    const hStr = h % 12 || 12, ampm = h < 12 ? 'AM' : 'PM';
    const mStr = m.toString().padStart(2, '0');
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return isToday ? `${hStr}:${mStr} ${ampm}` : `${d.toLocaleDateString('en', { month: 'short', day: 'numeric' })} at ${hStr}:${mStr} ${ampm}`;
  };

  const emailItems = [
    { key: 'taskAssigned',   label: intl.formatMessage({ id: 'NOTIF_TASK_ASSIGNED',  defaultMessage: 'Task assigned to you' }) },
    { key: 'taskCompleted',  label: intl.formatMessage({ id: 'NOTIF_TASK_COMPLETED', defaultMessage: 'Task completed' }) },
    { key: 'dueDateChanged', label: intl.formatMessage({ id: 'NOTIF_DUE_DATE',       defaultMessage: 'Due date changed' }) },
    { key: 'comments',       label: intl.formatMessage({ id: 'NOTIF_COMMENTS',       defaultMessage: 'Comments on your task' }) },
    { key: 'mentions',       label: intl.formatMessage({ id: 'NOTIF_MENTIONS',       defaultMessage: '@Mentions' }) },
    { key: 'dailyAgenda',    label: intl.formatMessage({ id: 'NOTIF_DAILY_AGENDA',   defaultMessage: 'Daily agenda (Mon–Fri)' }) },
    { key: 'weeklyOverdue',  label: intl.formatMessage({ id: 'NOTIF_WEEKLY_OVERDUE', defaultMessage: 'Weekly overdue digest' }) },
  ];

  return (
    <div>
      <AccordionSection
        defaultOpen
        icon={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M2.5 12.5h11M3 12.5V7a5 5 0 0110 0v5.5M6 12.5a2 2 0 004 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        }
        title={intl.formatMessage({ id: 'EMAIL_NOTIFICATIONS', defaultMessage: 'Email notifications' })}
        subtitle={intl.formatMessage({ id: 'EMAIL_NOTIF_SUBTITLE', defaultMessage: 'Customize what emails you receive' })}
      >
        {emailItems.map(({ key, label }, i) => (
          <div key={key} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px',
            borderBottom: i < emailItems.length - 1 ? '1px solid rgba(15,15,25,0.06)' : 'none',
          }}>
            <span style={{ fontSize: 13, color: 'rgba(15,15,25,0.72)' }}>{label}</span>
            <Switch checked={!!emailPrefs[key]} onChange={() => toggleEmail(key)}
              style={emailPrefs[key] ? { backgroundColor: '#6366f1' } : {}} size="small" />
          </div>
        ))}
      </AccordionSection>

      <AccordionSection
        defaultOpen={dnd.enabled || (pausedUntil && pausedUntil > new Date())}
        icon={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2a4.5 4.5 0 00-4.5 4.5c0 2.5-1 3.5-1 3.5h11s-1-1-1-3.5A4.5 4.5 0 008 2Z" stroke="currentColor" strokeWidth="1.3" fill="none" />
            <path d="M9.3 13.5a1.5 1.5 0 01-2.6 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        }
        title={intl.formatMessage({ id: 'DO_NOT_DISTURB', defaultMessage: 'Do not disturb' })}
        subtitle={intl.formatMessage({ id: 'DND_SUBTITLE', defaultMessage: 'Pause notifications during set hours' })}
      >
        {/* Pause / Resume banner */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(15,15,25,0.06)' }}>
          {isPaused ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1' }}>
                  {intl.formatMessage({ id: 'NOTIFS_PAUSED', defaultMessage: 'Notifications paused' })}
                </span>
                <span style={{ fontSize: 12, color: 'rgba(15,15,25,0.45)', marginLeft: 6 }}>
                  {intl.formatMessage({ id: 'UNTIL', defaultMessage: 'until' })} {fmtPausedUntil(pausedUntil)}
                </span>
              </div>
              <button onClick={handleResume} style={{
                height: 28, padding: '0 12px', fontSize: 12, fontWeight: 600,
                background: 'rgba(99,102,241,0.1)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.25)',
                borderRadius: 6, cursor: 'pointer',
              }}>
                {intl.formatMessage({ id: 'RESUME', defaultMessage: 'Resume' })}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: 'rgba(15,15,25,0.72)' }}>
                {intl.formatMessage({ id: 'PAUSE_NOTIFICATIONS', defaultMessage: 'Pause notifications' })}
              </span>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: PAUSE_OPTIONS.map(o => ({ key: o.key, label: o.label, onClick: () => handlePause(o.key) })),
                }}
                placement="bottomRight"
              >
                <button style={{
                  height: 28, padding: '0 12px', fontSize: 12, fontWeight: 600,
                  background: 'white', color: 'rgba(15,15,25,0.7)', border: '1px solid rgba(15,15,25,0.18)',
                  borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,15,25,0.18)'}
                >
                  {intl.formatMessage({ id: 'PAUSE', defaultMessage: 'Pause' })}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </Dropdown>
            </div>
          )}
        </div>

        {/* Schedule toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid rgba(15,15,25,0.06)' }}>
          <span style={{ fontSize: 13, color: 'rgba(15,15,25,0.72)' }}>
            {intl.formatMessage({ id: 'SCHEDULE_DND', defaultMessage: 'Schedule do not disturb' })}
          </span>
          <Switch checked={dnd.enabled} onChange={toggleDndEnabled}
            style={dnd.enabled ? { backgroundColor: '#6366f1' } : {}} size="small" />
        </div>

        {dnd.enabled && (
          <>
            <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(15,15,25,0.06)' }}>
              <p style={{ fontSize: 11, color: 'rgba(15,15,25,0.38)', marginBottom: 6, fontWeight: 500 }}>
                {intl.formatMessage({ id: 'DND_TIME_RANGE', defaultMessage: 'Do not notify me from' })}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TimeSelect value={dnd.from} onChange={v => handleDndTime('from', v)} />
                <span style={{ fontSize: 12, color: 'rgba(15,15,25,0.38)', flexShrink: 0 }}>{intl.formatMessage({ id: 'TO', defaultMessage: 'to' })}</span>
                <TimeSelect value={dnd.to} onChange={v => handleDndTime('to', v)} />
              </div>
            </div>
            <div style={{ padding: '8px 14px' }}>
              <p style={{ fontSize: 11, color: 'rgba(15,15,25,0.38)', marginBottom: 6, fontWeight: 500 }}>
                {intl.formatMessage({ id: 'DND_DAYS', defaultMessage: 'Days' })}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {DAYS.map(day => (
                  <button key={day} onClick={() => toggleDndDay(day)} style={{
                    padding: '3px 9px', borderRadius: 5, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    ...(dnd.days.includes(day)
                      ? { background: '#6366f1', color: 'white', border: '1px solid #6366f1' }
                      : { background: 'rgba(99,102,241,0.05)', color: 'rgba(15,15,25,0.55)', border: '1px solid rgba(15,15,25,0.12)' }),
                  }}>
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </AccordionSection>
    </div>
  );
};

/* ─── Main SettingsModal ─────────────────────────────────────────────────── */
const TAB_LABELS = {
  profile:       { id: 'SETTINGS_TAB_PROFILE',       defaultMessage: 'Profile' },
  notifications: { id: 'SETTINGS_TAB_NOTIFICATIONS', defaultMessage: 'Notifications' },
  integrations:  { id: 'SETTINGS_TAB_INTEGRATIONS',  defaultMessage: 'Integrations & API' },
};

// One size for every tab: the modal never changes size when switching tabs,
// the body scrolls instead. The height only shrinks on short screens.
const MODAL_WIDTH = 680;
const BODY_HEIGHT = 'min(480px, calc(100vh - 200px))';

// Integrations & API: AI assistants (MCP), connected apps and access tokens -
// the same components as the /integrations page, in their compact form
// (laid out for the modal's width), with the modal's indigo accent.
const IntegrationsTab = () => (
  <ConfigProvider theme={{ token: { colorPrimary: '#6366f1' } }}>
    <div className="flex flex-col gap-3 py-1">
      <McpConnectGuide compact />
      <ConnectedAppsSettings compact />
      <PersonalAccessTokensSettings compact />
    </div>
  </ConfigProvider>
);

const SettingsModal = ({ open, onClose, user, defaultTab = 'profile' }) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'

  useEffect(() => { if (open) setActiveTab(defaultTab); }, [open, defaultTab]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={MODAL_WIDTH}
      centered
      title={null}
      closable={false}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ background: 'white', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px 0', borderBottom: '1px solid rgba(15,15,25,0.1)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'rgba(15,15,25,0.88)', margin: 0, paddingBottom: 12 }}>
            {intl.formatMessage({ id: 'SETTINGS', defaultMessage: 'Settings' })}
          </h2>
          <button onClick={onClose} style={{
            width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(15,15,25,0.4)',
            marginBottom: 12,
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs + save toast anchor */}
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: 0, padding: '0 20px', borderBottom: '1px solid rgba(15,15,25,0.1)' }}>
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                position: 'relative', padding: '10px 4px', marginRight: 18, fontSize: 13, fontWeight: 500,
                color: activeTab === tab ? '#6366f1' : 'rgba(15,15,25,0.45)', background: 'none', border: 'none', cursor: 'pointer',
              }}>
                {intl.formatMessage(TAB_LABELS[tab])}
                {activeTab === tab && (
                  <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: '#6366f1', borderRadius: 2 }} />
                )}
              </button>
            ))}
          </div>

          {saveStatus !== 'idle' && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '5px 20px',
              background: saveStatus === 'saving' ? 'rgba(99,102,241,0.07)' : 'rgba(34,197,94,0.07)',
              pointerEvents: 'none',
            }}>
              {saveStatus === 'saving' ? (
                <>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>
                    {intl.formatMessage({ id: 'SAVING', defaultMessage: 'Saving…' })}
                  </span>
                </>
              ) : (
                <>
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6.5l3 3 5-6" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 500 }}>
                    {intl.formatMessage({ id: 'SAVED', defaultMessage: 'Saved' })}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Body — fixed height */}
        <div style={{ padding: '10px 20px', overflowY: 'auto', height: BODY_HEIGHT }}>
          {activeTab === 'profile'       && <ProfileTab user={user} onClose={onClose} />}
          {activeTab === 'notifications' && <NotificationsTab onSavingChange={setSaveStatus} />}
          {activeTab === 'integrations'  && <IntegrationsTab />}
        </div>
      </div>
    </Modal>
  );
};

export default SettingsModal;
